import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { capitalize, compact, find, groupBy, isNull, without } from 'lodash';
import { Link } from 'react-router-dom';

import CardLayout from '@magoosh/layouts/card';
import { BarChart } from '@magoosh/lib/charts';
import {
  HighSignificanceIcon,
  LowSignificanceIcon,
  ModerateSignificanceIcon
} from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';

import { ConceptData } from './types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  data: ConceptData[]; // TODO be specific about data format
  period: string;
  section: string;
}

const PerformanceByConcept: React.FC<Props> = (props) => {
  const [minPercentCorrect, setMinPercentCorrect] = useState(0);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [])

  const dataPointGroups = () => {
    const dataPoints = without(props.data, overallDataPoint());

    const conceptsByFrequency = groupBy(dataPoints, 'frequency');

    return compact([
      conceptsByFrequency.high && { frequency: 'high', concepts: conceptsByFrequency.high },
      conceptsByFrequency.medium && { frequency: 'medium', concepts: conceptsByFrequency.medium },
      conceptsByFrequency.low && { frequency: 'low', concepts: conceptsByFrequency.low }
    ]);
  };

  const handleResize = () => {
    if (window.innerWidth >= 1200) {
      setMinPercentCorrect(8);
    } else if (window.innerWidth >= 992) {
      setMinPercentCorrect(12);
    } else {
      setMinPercentCorrect(20);
    }
  };

  const overallDataPoint = () => {
    return find(props.data, (datum) => datum.name === 'Overall');
  };

  const renderChart = (dataPoint) => {
    let chartData;

    if (dataPoint.answers.total) {
      const percentCorrect = Math.max(dataPoint.answers.percentCorrect, minPercentCorrect);

      chartData = [
        {
          concept: dataPoint.name,
          'Correct': dataPoint.answers.total ? percentCorrect : 0,
          'CorrectColor': colors.dataDarkBlue,
          'Incorrect': 100 - percentCorrect,
          'IncorrectColor': colors.dataMutedBlue
        }
      ];
    } else {
      chartData = [
        {
          concept: dataPoint.name,
          'No Activity': 100,
          'No ActivityColor': colors.dataMutedBlue
        }
      ];
    }

    return (
      <div style={{ position: 'relative' }}>
        <BarChart
          borderRadius={8}
          chartProps={{
            animate: false,
            margin: { left: 0 },
            padding: 0.15,
            tooltip: (datum) => {
              let content;
              if (datum.id === 'Incorrect') {
                content = <>{datum.label}: <strong>{100 - dataPoint.answers.percentCorrect}%</strong></>;
              } else if (datum.id === 'No Activity') {
                content = 'No Activity';
              } else {
                content = <>{datum.label}: <strong>{dataPoint.answers.percentCorrect}%</strong></>;
              }

              return (
                <div
                  className="align-items-center d-flex gap-xs"
                  style={{
                    background: 'white',
                    color: 'inherit',
                    fontSize: 'inherit',
                    borderRadius: '2px',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                    padding: '5px 9px'
                  }}
                >
                  <span style={{ display: 'block', width: 12, height: 12, background: datum.color }} />
                  {content}
                </div>
              );
            }
          }}
          data={chartData}
          hideLegend
          horizontal
          indexBy="concept"
        />

        <div
          style={{
            color: dataPoint.answers.total ? '#ffffff' : colors.dataDarkBlue,
            fontSize: 12,
            fontWeight: 'bold',
            left: 10,
            top: '50%',
            position: 'absolute',
            transform: 'translateY(-50%)',
            zIndex: 10
          }}
        >
          {renderLabel(dataPoint)}
        </div>
      </div>
    );
  };

  const renderLabel = (datum) => {
    if (datum.answers.total === 0) {
      return 'No Activity';
    } else {
      return `${datum.answers.percentCorrect}%`;
    }
  };

  return (
    <div className="d-flex">
      <div
        className="hidden-sm hidden-xs"
        style={{
          borderRight: `1px solid ${colors.gray85}`,
          width: 30
        }}
      />

      <div className="d-flex flex-1 flex-column gap-m">
        {
          dataPointGroups().map((group, idx) => {
            const FrequencyIcon = {
              high: HighSignificanceIcon,
              low: LowSignificanceIcon,
              medium: ModerateSignificanceIcon
            }[group.frequency];

            return (
              <div key={idx} className="d-flex flex-1">
                <div
                  className="hidden-sm hidden-xs"
                  style={{
                    borderBottom: `1px solid ${colors.gray85}`,
                    height: 40,
                    marginRight: 15,
                    width: 20
                  }}
                />

                <CardLayout.Card key={idx} className={cx(style.card, 'd-flex flex-1 flex-column gap-m')}>
                  <div className="d-flex">
                    <div className="text-underdash">
                      <FrequencyIcon fill={colors.dataLightPurple} height={15} width={19} />
                      {' '}<strong>{capitalize(group.frequency)} Frequency {capitalize(props.section)} Concepts</strong>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-m">
                    {
                      group.concepts.map((concept) => {
                        const detailPath = paths.analyticsDetail(
                          props.section,
                          concept.slug,
                          { period: props.period }
                        );

                        return (
                          <div
                            key={concept.name}
                            className="align-items-center d-flex gap-m gap-lg-l"
                          >
                            <div className="align-items-lg-center d-flex flex-1 flex-column flex-md-row gap-xs gap-md-l">
                              <div className={style.conceptName}>
                                <div className="visible-xs">
                                  <span><strong>{concept.name}</strong></span>
                                  <span className="text-gray-light u-margin-L-xs" style={{ fontSize: 12 }}>
                                    {concept.answers.total ? `${concept.answers.correct} of ${concept.answers.total}` : 'No Activity'}
                                  </span>
                                </div>

                                <div className="hidden-xs">
                                  <strong>{concept.name}</strong>

                                  <div className="text-gray-light" style={{ fontSize: 12 }}>
                                    {concept.answers.total ? `${concept.answers.correct} of ${concept.answers.total}` : 'No Activity'}
                                  </div>
                                </div>
                              </div>

                              <div className={cx(style.chart, 'flex-1')}>
                                {renderChart(concept)}
                              </div>
                            </div>

                            <div>
                              <Link className={cx('btn btn-default btn-sm', style.detailsButton)} to={detailPath}>
                                View<span className="hidden-xs"> Breakdown</span>
                              </Link>
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>
                </CardLayout.Card>
              </div>
            )
          })
        }
      </div>
    </div>
  );
};

export default PerformanceByConcept;
